<template>
  <v-switch
    v-model="localValue.realTimeMalwareProtectionEnabled"
    color="primary"
    inset
    density="compact"
    hide-details
    data-testid="devices-settings-realtime-malware-protection-enabled-checkbox"
    :ripple="false"
    class="mb-4"
    :disabled="actionNotAllowed"
    @click.native.prevent.stop.capture="
      $emit('change-risky-ngav-item', 'realTimeMalwareProtectionEnabled')
    "
  >
    <template #prepend>
      <span class="subtitle1 item-clickable mr-2">{{
        $t("devicesSettings.settingsCard.realTimeMalwareProtection")
      }}</span>
    </template>
  </v-switch>
  <v-card class="h-100">
    <v-card-text class="px-4 py-6">
      <v-checkbox
        v-model="localValue.atcEnabled"
        data-testid="enable-atc-checkbox"
        :disabled="isRealTimeMalwareProtectionDisabled"
        :ripple="false"
        class="mt-3"
        @click.native.prevent.stop.capture="$emit('change-risky-ngav-item', 'atcEnabled')"
      >
        <template #label>
          <div class="subtitle1">
            {{ $t("devicesSettings.settingsCard.atcEnabled") }}
          </div>
        </template>
      </v-checkbox>
      <div class="ml-7">
        <div class="body2 text-indigo-medium w-75">
          {{ $t("devicesSettings.settingsCard.atcDescription") }}
        </div>
        <template v-if="localValue.atcEnabled">
          <v-autocomplete
            v-model="localValue.atcLabels"
            class="mt-4"
            :items="labels"
            :label="deviceLabelsPlaceholder(localValue.atcLabels?.length)"
            :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
            :disabled="isParentSettingDisabled(localValue.atcEnabled)"
            variant="outlined"
            multiple
            return-object
            data-testid="devices-settings-enable-atc-labels"
            item-value="id"
            item-title="name"
            hide-details="auto"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                size="default"
                :color="item.raw.color"
                close-icon="$x"
                label
                rounded="3"
                variant="flat"
                :text="item.raw.name"
                :disabled="isParentSettingDisabled(localValue.atcEnabled)"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ item.raw.name }}
                </span>
              </v-chip>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props" class="label-popup-block" title="">
                <div class="d-flex align-center">
                  <span
                    class="color-preview mr-2"
                    :style="{ 'background-color': item.raw.color }"
                  />
                  <span
                    :data-testid="`devices-settings-enable-atc-${kebabCase(item.raw.name)}-select-item`"
                    >{{ item.raw.name }}
                  </span>
                </div>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div class="d-flex align-center pl-4 mt-1">
            <v-icon class="mr-1" size="18" icon="$circleCheck" />
            <span class="caption text-green-dark">
              {{ $t("devicesSettings.settingsCard.labelsFieldHint") }}
            </span>
          </div>
        </template>
      </div>

      <v-checkbox
        v-model="localValue.vssPolicyEnabled"
        data-testid="devices-settings-vcc-policy-enabled-checkbox"
        :disabled="isRealTimeMalwareProtectionDisabled"
        :ripple="false"
        class="mt-6"
        @click.native.prevent.stop.capture="$emit('change-risky-ngav-item', 'vssPolicyEnabled')"
      >
        <template #label>
          <div class="subtitle1">
            {{ $t("devicesSettings.settingsCard.vssPolicyEnabled") }}
          </div>
        </template>
      </v-checkbox>
      <div class="ml-7">
        <div class="body2 text-indigo-medium w-75">
          {{ $t("devicesSettings.settingsCard.vssPolicyDescription") }}
        </div>
        <template v-if="localValue.vssPolicyEnabled">
          <v-autocomplete
            v-model="localValue.vssPolicyLabels"
            class="mt-4"
            :items="labels"
            :label="deviceLabelsPlaceholder(modelValue?.vssPolicyLabels?.length)"
            :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
            :disabled="isParentSettingDisabled(localValue.vssPolicyEnabled)"
            variant="outlined"
            multiple
            return-object
            data-testid="devices-settings-enable-vcc-labels"
            item-value="id"
            item-title="name"
            hide-details="auto"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                size="default"
                :color="item.raw.color"
                close-icon="$x"
                label
                rounded="3"
                variant="flat"
                :text="item.raw.name"
                :disabled="isParentSettingDisabled(localValue.vssPolicyEnabled)"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ item.raw.name }}
                </span>
              </v-chip>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props" title="">
                <v-list-item class="label-popup-block">
                  <div class="d-flex align-center">
                    <span
                      class="color-preview mr-2"
                      :style="{ 'background-color': item.raw.color }"
                    />
                    <span
                      :data-testid="`devices-settings-enable-vcc-${kebabCase(item.raw.name)}-select-item`"
                    >
                      {{ item.raw.name }}
                    </span>
                  </div>
                </v-list-item>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div class="d-flex align-center pl-4 mt-1">
            <v-icon class="mr-1" size="18" icon="$circleCheck" />
            <span class="caption text-green-dark">
              {{ $t("devicesSettings.settingsCard.labelsFieldHint") }}
            </span>
          </div>
        </template>
      </div>

      <v-checkbox
        v-model="localValue.enforceContainerQuarantineEnabled"
        data-testid="devices-settings-enable-edr-block-checkbox"
        :ripple="false"
        class="mt-6"
        :disabled="isRealTimeMalwareProtectionDisabled"
      >
        <template #label>
          <div class="subtitle1">
            {{ $t("devicesSettings.settingsCard.enforceContainerQuarantine") }}
          </div>
        </template>
      </v-checkbox>
      <div class="ml-7">
        <div class="body2 text-indigo-medium w-75">
          {{ $t("devicesSettings.settingsCard.enforceContainerQuarantineDescription") }}
        </div>
        <template v-if="localValue.enforceContainerQuarantineEnabled">
          <v-autocomplete
            v-model="localValue.enforceContainerQuarantineLabels"
            class="mt-4"
            :items="labels"
            :label="deviceLabelsPlaceholder(localValue.enforceContainerQuarantineLabels?.length)"
            :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
            :disabled="isParentSettingDisabled(localValue.enforceContainerQuarantineEnabled)"
            variant="outlined"
            multiple
            return-object
            data-testid="devices-settings-enable-malware-labels"
            item-value="id"
            item-title="name"
            hide-details="auto"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                size="default"
                :color="item.raw.color"
                close-icon="$x"
                label
                rounded="3"
                variant="flat"
                :text="item.raw.name"
                :disabled="isParentSettingDisabled(localValue.enforceContainerQuarantineEnabled)"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ item.raw.name }}
                </span>
              </v-chip>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props" class="label-popup-block" title="">
                <div class="d-flex align-center">
                  <span
                    class="color-preview mr-2"
                    :style="{ 'background-color': item.raw.color }"
                  />
                  <span
                    :data-testid="`devices-settings-enable-malware-${kebabCase(
                      item.raw.name
                    )}-select-item`"
                  >
                    {{ item.raw.name }}
                  </span>
                </div>
              </v-list-item>
            </template>
          </v-autocomplete>

          <div class="d-flex align-center pl-4 mt-1">
            <v-icon class="mr-1" size="18" icon="$circleCheck" />
            <span class="caption text-green-dark">
              {{ $t("devicesSettings.settingsCard.labelsFieldHint") }}
            </span>
          </div>
        </template>
      </div>

      <v-checkbox
        v-model="localValue.initialDevicesScanEnabled"
        data-testid="devices-settings-initial-scan-checkbox"
        :disabled="isRealTimeMalwareProtectionDisabled"
        :ripple="false"
        class="mt-6"
        @click.native.prevent.stop.capture="$emit('change-initial-scan')"
      >
        <template #label>
          <div class="subtitle1">
            {{ $t("devicesSettings.settingsCard.initialDevicesScan") }}
          </div>
        </template>
      </v-checkbox>
      <div class="ml-7">
        <div class="body2 text-indigo-medium w-75">
          {{ $t("devicesSettings.settingsCard.initialDevicesScanDescription") }}
        </div>
        <template v-if="localValue.initialDevicesScanEnabled">
          <v-autocomplete
            v-model="localValue.initialDevicesScanLabels"
            class="mt-4"
            :items="labels"
            :label="deviceLabelsPlaceholder(localValue.initialDevicesScanLabels?.length)"
            :placeholder="$t('devicesSettings.settingsCard.deviceLabelsPlaceholder')"
            :disabled="isParentSettingDisabled(localValue.initialDevicesScanEnabled)"
            variant="outlined"
            multiple
            return-object
            data-testid="devices-settings-initial-scan-labels"
            item-value="id"
            item-title="name"
            hide-details="auto"
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                :closable="true"
                size="default"
                :color="item.raw.color"
                close-icon="$x"
                label
                rounded="3"
                variant="flat"
                :text="item.raw.name"
                :disabled="isParentSettingDisabled(localValue.initialDevicesScanEnabled)"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ item.raw.name }}
                </span>
              </v-chip>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props" title="" class="label-popup-block">
                <div class="d-flex align-center">
                  <span
                    class="color-preview mr-2"
                    :style="{ 'background-color': item.raw.color }"
                  />
                  <span
                    :data-testid="`devices-settings-initial-scan-${kebabCase(item.raw.name)}-select-item`"
                    >{{ item.raw.name }}</span
                  >
                </div>
              </v-list-item>
            </template>
          </v-autocomplete>

          <div class="d-flex align-center pl-4 mt-1">
            <v-icon class="mr-1" size="18" icon="$circleCheck" />
            <span class="caption text-green-dark">
              {{ $t("devicesSettings.settingsCard.labelsFieldHint") }}
            </span>
          </div>
        </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { kebabCase } from "lodash";
import { defineComponent, watch, ref, computed, type PropType } from "vue";
import type { DeviceLabel, NgavSettings } from "@/_store/devices-settings.module.ts";
import isEqual from "lodash/isEqual";
import { useI18n } from "vue-i18n";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<NgavSettings>,
      required: true,
    },
    labels: {
      type: Array as PropType<DeviceLabel[]>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue", "change-risky-ngav-item", "change-initial-scan"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const localValue = ref({ ...props.modelValue });

    const deviceLabelsPlaceholder = (labelsLength: number) => {
      return labelsLength ? i18n.t("devicesSettings.settingsCard.deviceLabelsLabel") : "";
    };

    const isRealTimeMalwareProtectionDisabled = computed(() => {
      return props.actionNotAllowed || !localValue.value.realTimeMalwareProtectionEnabled;
    });

    const isParentSettingDisabled = (parentSettingEnabled: boolean) => {
      return isRealTimeMalwareProtectionDisabled.value || !parentSettingEnabled;
    };

    watch(
      () => localValue.value,
      (newValue) => {
        if (!isEqual(newValue, props.modelValue)) {
          emit("update:modelValue", newValue);
        }
      },
      { deep: true }
    );

    watch(
      () => props.modelValue,
      (newValue) => {
        localValue.value = { ...newValue };
      },
      { deep: true }
    );

    return {
      isRealTimeMalwareProtectionDisabled,
      localValue,
      kebabCase,
      deviceLabelsPlaceholder,
      isParentSettingDisabled,
    };
  },
});
</script>

<style lang="scss" scoped>
.label-popup-block {
  .v-list-item__content {
    display: flex;
    align-items: center;
  }
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.v-label {
  font-size: 18px !important;
}

.autocomplete-label {
  opacity: var(--v-disabled-opacity);
}
</style>
